import type { WhatsAppIntegration } from 'src/api/generated';
import type { WhatsAppIntegrationOnboardingSafety } from '../../types/whatsapp-integration-onboarding-safety.type';
import { isNonBlankString, isRecord } from '../types.util';
import { isParseableDateString } from '../validation/date.util';

type UnknownRecord = Record<string, unknown>;

const verificationSources = new Set(['meta_solutions', 'meta_account_update']);

const isValidEvidenceBase = (verification: UnknownRecord): boolean =>
  verificationSources.has(String(verification.source)) &&
  isNonBlankString(verification.expectedSolutionId) &&
  typeof verification.expectedPresent === 'boolean' &&
  isParseableDateString(verification.checkedAt);

const unsafe = (
  isComosManaged: boolean
): WhatsAppIntegrationOnboardingSafety => ({
  isComosManaged,
  isUnsafe: true,
  canDelete: false,
});

/**
 * Classifies persisted WhatsApp onboarding safety without trusting compile-time API
 * types. Unknown or incomplete new-format records fail closed, while true
 * legacy records remain removable for backwards compatibility.
 */
export const classifyWhatsAppIntegrationOnboardingSafety = (
  integration: Pick<WhatsAppIntegration, 'integrationFields'>
): WhatsAppIntegrationOnboardingSafety => {
  const fields: unknown = integration.integrationFields;
  if (!isRecord(fields)) return unsafe(false);

  const billing = fields.billing;
  const verification = fields.solutionAssociationVerification;

  // A retired evidence key is not a legacy account with no provenance.
  if (Object.prototype.hasOwnProperty.call(fields, 'billingVerification')) {
    return unsafe(isRecord(billing) && billing.mode === 'comos_managed');
  }

  if (billing === undefined && verification === undefined) {
    return { isComosManaged: false, isUnsafe: false, canDelete: true };
  }

  const rawMode = isRecord(billing) ? billing.mode : undefined;
  const isComosManaged = rawMode === 'comos_managed';
  if (!isRecord(billing) || !isRecord(verification)) {
    return unsafe(isComosManaged);
  }

  if (!isValidEvidenceBase(verification)) {
    return unsafe(isComosManaged);
  }

  if (typeof verification.observedPresent !== 'boolean') {
    return unsafe(isComosManaged);
  }

  if (
    verification.status !== 'verified' ||
    verification.observedPresent !== verification.expectedPresent
  ) {
    return unsafe(isComosManaged);
  }

  if (rawMode === 'customer_managed') {
    return verification.expectedPresent === false
      ? { isComosManaged: false, isUnsafe: false, canDelete: true }
      : unsafe(false);
  }

  if (rawMode !== 'comos_managed') return unsafe(false);

  const managedBilling = billing.managedBilling;
  if (
    !isRecord(managedBilling) ||
    !isNonBlankString(managedBilling.provider) ||
    !isNonBlankString(managedBilling.solutionId) ||
    !isNonBlankString(managedBilling.onboardingAttemptId) ||
    verification.expectedPresent !== true ||
    verification.expectedSolutionId !== managedBilling.solutionId
  ) {
    return unsafe(true);
  }

  return { isComosManaged: true, isUnsafe: false, canDelete: false };
};
